import { MerchantConfigurationStatus, ProvisioningStep } from '@/enums/SmoothrPay';
import { MerchantConfiguration } from '@/interfaces/models/SmoothrPay';

/** Display order — also the execution order. */
export const PROVISIONING_STEPS: ProvisioningStep[] = [
  ProvisioningStep.LEGAL_ENTITY,
  ProvisioningStep.BUSINESS_LINES,
  ProvisioningStep.ACCOUNT_HOLDER,
  ProvisioningStep.BALANCE_ACCOUNTS,
  ProvisioningStep.KYC,
];

/**
 * Whether a provisioning step already ran, judged by the Adyen ids the service
 * writes back onto the configuration. Deriving this from the document (rather
 * than tracking local step state) means a half-provisioned configuration
 * resumes correctly after a reload or a failed step.
 *
 * KYC has no id of its own: Adyen reports progress through the account-holder
 * webhook, so `onboarding`/`active` is the closest signal that the merchant has
 * been handed off.
 */
export function isStepComplete(config: MerchantConfiguration | null, step: ProvisioningStep): boolean {
  if (!config) {
    return false;
  }
  switch (step) {
    case ProvisioningStep.LEGAL_ENTITY:
      return !!config.legalEntity && !!config.legalEntity.id;
    case ProvisioningStep.BUSINESS_LINES:
      // Every line must exist at Adyen — a partial run has to be repeatable.
      return !!config.businessLines && config.businessLines.length > 0 && config.businessLines.every((l) => !!l.id);
    case ProvisioningStep.ACCOUNT_HOLDER:
      return !!config.accountHolderId;
    case ProvisioningStep.BALANCE_ACCOUNTS:
      return (
        !!config.balanceAccounts && config.balanceAccounts.length > 0 && config.balanceAccounts.every((a) => !!a.id)
      );
    case ProvisioningStep.KYC:
      return (
        config.status === MerchantConfigurationStatus.ONBOARDING || config.status === MerchantConfigurationStatus.ACTIVE
      );
    default:
      return false;
  }
}

/** The next step to run, or `null` when the sequence is exhausted. */
export function nextProvisioningStep(config: MerchantConfiguration | null): ProvisioningStep | null {
  if (!config) {
    return null;
  }
  return PROVISIONING_STEPS.find((step) => !isStepComplete(config, step)) || null;
}

/**
 * Whether a step may be triggered right now. A `closed` or `suspended`
 * configuration is read-only, and the service rejects the KYC link before the
 * legal entity exists — so the button must not offer it either. KYC itself can
 * be regenerated at will once provisioning is done, since the link expires
 * within minutes.
 */
export function canRunStep(config: MerchantConfiguration | null, step: ProvisioningStep): boolean {
  if (!config) {
    return false;
  }
  if (config.status === MerchantConfigurationStatus.CLOSED || config.status === MerchantConfigurationStatus.SUSPENDED) {
    return false;
  }
  if (step === ProvisioningStep.KYC) {
    return PROVISIONING_STEPS.filter((s) => s !== ProvisioningStep.KYC).every((s) => isStepComplete(config, s));
  }
  return nextProvisioningStep(config) === step;
}

/** True once every Adyen resource exists, i.e. KYC can be started. */
export function isProvisioned(config: MerchantConfiguration | null): boolean {
  return canRunStep(config, ProvisioningStep.KYC);
}

/** A configuration that is neither closed nor suspended is editable. */
export function isConfigurationOpen(config: MerchantConfiguration | null): boolean {
  if (!config || !config.status) {
    return false;
  }
  return (
    config.status !== MerchantConfigurationStatus.CLOSED && config.status !== MerchantConfigurationStatus.SUSPENDED
  );
}
